import { Fragment } from 'react';

import { Checkbox, Grid, Box, Typography } from '@mui/material';

import { IMedication } from 'apis/survey/type';
import { IFormValues, IFormWatch } from 'routes/Main/type';
import { DateBoundTimePicker } from 'components/TimePicker/DateBoundTimePicker';
import { coercePickerDate } from 'utils/timePickerBridge';

import SectionTitle from '../components/SectionTitle';
import MuiTable from 'components/MuiTable';
import { formatStringToDate } from 'utils/formatting';

import { ReactComponent as DrugInfo } from 'assets/drug-info.svg';
import { ReactComponent as NaverDrugInfo } from 'assets/naver-drug.svg';
import MuiModal from './MuiModal';

interface Props extends IFormValues, IFormWatch {
  disabled?: boolean;
}

const Medications = (props: Props) => {
  const { disabled, watch, getValues, setValue } = props;
  const medicationList: IMedication[] = getValues('medication_surveys');

  const columns = [
    { fieldId: 'pt_medication_no', label: '약물처방번호' },
    { fieldId: 'prescription_time', label: '처방시간' },
    { fieldId: 'medication_time', label: '액팅시간' },
    { fieldId: 'medication_name', label: '약물명', width: 150 },
    { fieldId: 'medication_content', label: '함량' },
    { fieldId: 'medication_measure', label: '단위' },
    { fieldId: 'medication_amount', label: '투여량' },
    { fieldId: 'medication_freq', label: '투여횟수' },
    { fieldId: 'medication_note', label: '상세투여방법', width: 250 },
    { fieldId: 'medication_do', label: '투여완료' },
    { fieldId: 'medication_info', label: '약물정보1' },
    { fieldId: 'medication_info_2', label: '약물정보2' },
  ];

  const getRowEditing = (prefix: string) => {
    const medicationTime = watch(`${prefix}.medication_time`);
    const onChangeMedicationTime = (value: any) =>
      setValue(`${prefix}.medication_time`, value);
    const medicationDo = Boolean(watch(`${prefix}.medication_do`));
    const onChangeMedicationDo = (_: any, checked: boolean) =>
      setValue(`${prefix}.medication_do`, checked ? 1 : 0);

    return {
      medicationTime,
      onChangeMedicationTime,
      medicationDo,
      onChangeMedicationDo,
    };
  };

  const rows = (medicationList ?? [])
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => !item.is_hidden)
    .map(({ item, index }) => {
      const {
        medicationTime,
        onChangeMedicationTime,
        medicationDo,
        onChangeMedicationDo,
      } = getRowEditing(`medication_surveys.${index}`);

      return {
        ...item,
        id: item.pt_medication_no,
        prescription_time: formatStringToDate(
          item.prescription_time,
          'hh:mm a'
        ),
        medication_time: (
          <DateBoundTimePicker
            disabled={disabled}
            value={coercePickerDate(medicationTime)}
            onChange={onChangeMedicationTime}
            placeholder="00:00"
            usePortal={false}
            sx={{ width: 150 }}
          />
        ),
        medication_do: (
          <Checkbox
            size="small"
            disabled={disabled}
            checked={medicationDo}
            onChange={onChangeMedicationDo}
            sx={{ marginLeft: '4px' }}
          />
        ),
        medication_info: (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DrugInfo
              style={{ cursor: 'pointer', marginLeft: '-10px' }}
              onClick={() =>
                window.open(
                  'https://nedrug.mfds.go.kr/search?keyword=' +
                    item.medication_name.replaceAll('%', '').replace(/%20/g, '')
                )
              }
            />
          </Box>
        ),
        medication_info_2: (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <NaverDrugInfo
              style={{ cursor: 'pointer', marginLeft: '-10px' }}
              onClick={() =>
                window.open(
                  'https://www.connectdi.com/cont/drug/?pap=search_result&search_keyword_type=all&search_keyword=' +
                    item.medication_name
                )
              }
            />
          </Box>
        ),
      };
    });

  return (
    <Fragment>
      <SectionTitle title="처방 내역" />
      <Grid item xs={12}>
        <MuiTable columns={columns} rows={rows} />
        <div style={{ paddingLeft: '90%' }}>
          <MuiModal
            disabled={disabled}
            getValues={getValues}
            setValue={setValue}
          />
        </div>
      </Grid>
      <Grid item xs={12}>
        <Typography
          sx={{ fontSize: '12px', lineHeight: '24px', marginLeft: '15px' }}
        >
          💡 약물 정보 검색 주의 사항 안내
          <br />
          스마트널스ENR은 건강보험심사평가원 데이터를 활용합니다. 해당 약품
          데이터는 성분명으로 제공되어 검색 시 어려움이 있을 수 있습니다.
          <br />
          이를 보완하여 상세 투여 방법에 유통되는 대표 약품명을 기재 했습니다.
          <br />
          검색이 안 될 시 해당 약품명으로 검색해 주세요.
        </Typography>
      </Grid>
    </Fragment>
  );
};

export default Medications;
